import React, { useState } from "react";
import { IconArticle } from "./Icons";
import AssignModal from "./modals/AssignModal";

/**
 * EmployeeScreen Component
 * Displays individual employee page with diagnostics and recommendations
 * Props:
 * - employee: Employee object { id, name, email, role, position, avatar }
 * - companyName: Name of the company for back button
 * - employees: Array of all employees (for assign modal dropdown)
 * - onBack: Callback when back button is clicked
 */
export default function EmployeeScreen({
  employee,
  companyName,
  employees = [],
  onBack,
}) {
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState("");

  // TODO: Replace with API data
  const forms = [
    { id: 1, size: "Form 1 – 25 Questions", v: "V1.0" },
    { id: 2, size: "Form 24 – 50 Questions", v: "V1.0", featured: true },
    { id: 3, size: "Form 50 – 75 Questions", v: "V1.0" },
    { id: 4, size: "Form 1 – 25 Questions", v: "V1.0" },
    { id: 5, size: "Form 24 – 50 Questions", v: "V1.0" },
    { id: 6, size: "Form 50 – 75 Questions", v: "V1.0" },
  ];

  // TODO: Replace with API data
  const recommendations = [
    {
      id: 1,
      title: "Effective Communication In The Workspace",
      sub: "Learn strategies for enhancing communication",
    },
    {
      id: 2,
      title: "Time Management Techniques",
      sub: "Discover methods to improve your time management",
    },
    {
      id: 3,
      title: "Developing Leadership Skills",
      sub: "Improve your leadership capabilities",
    },
    {
      id: 4,
      title: "Critical Thinking And Problem Solving",
      sub: "Enhance your analytical capabilities",
    },
  ];

  const handleOpenAssignModal = (form) => {
    setSelectedForm(form);
    setAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setAssignModalOpen(false);
    setSelectedForm(null);
    setSelectedStaff("");
  };

  const handleConfirmAssign = () => {
    // TODO: Connect to API endpoint to assign form to selected employee
    console.log("Assigning form:", selectedForm, "to staff:", selectedStaff);
    handleCloseAssignModal();
  };

  const employeeOptions = employees.map((emp) => ({
    value: emp._id,
    label: `${emp.firstName} ${emp.lastName}`,
  }));

  return (
    <div className="cd-employee-page">
      <div className="welcome" onClick={onBack}>
        ← Back {companyName}
      </div>

      <div className="cd-emp-headerline">
        <h1 className="username">{employee.name}</h1>
        <span className="cd-emp-role">({employee.role})</span>
      </div>

      {/* Diagnostic Form block */}
      <div className="cd-emp-card">
        <div className="cd-emp-card-title">Diagnostic Form</div>
        <div className="cd-emp-subtitle">Assign from a Diagnostic Form</div>

        <div className="cd-diagnostic-grid cd-diagnostic-grid--employee">
          {forms.map((f) => (
            <div
              key={f.id}
              className={`cd-diagnostic-card ${f.featured ? "is-featured" : ""}`}
            >
              <div className="cd-diagnostic-check">
                <input type="checkbox" />
                <span>{f.size}</span>
                <span className="cd-version">{f.v}</span>
              </div>
              <div className="cd-diagnostic-desc sm">
                What good looks like: short blurb describing outcomes and
                observable behavior.
              </div>
              <div className="cd-diagnostic-actions">
                <button
                  className="cd-assign-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAssignModal(f);
                  }}
                >
                  Assign →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendation Drawer */}
      <div className="cd-emp-card">
        <div className="cd-emp-card-title-row">
          <div className="cd-emp-card-title">Recommendation Drawer</div>
          <button className="cd-browse-btn">Browse Resources →</button>
        </div>

        <div className="cd-emp-toolbar">
          <div className="cd-emp-tabs">
            {["Articles", "Videos", "Exercises", "Courses", "Workshops"].map(
              (t, i) => (
                <button
                  key={t}
                  className={`cd-emp-tab ${i === 0 ? "active" : ""}`}
                >
                  {t}
                </button>
              )
            )}
          </div>
        </div>

        <div className="cd-emp-filters">
          <div className="cd-search-wrapper">
            <input className="cd-search-input" placeholder="Search" />
            <span className="cd-search-hint">⌘K</span>
          </div>
          <select className="cd-select">
            <option>Competency</option>
          </select>
          <select className="cd-select">
            <option>Duration</option>
          </select>
        </div>

        <div className="cd-emp-list">
          {recommendations.map((r) => (
            <label key={r.id} className="cd-emp-row">
              <input type="checkbox" />
              <div className="cd-emp-row-icon">
                <IconArticle />
              </div>
              <div className="cd-emp-row-text">
                <div className="cd-emp-row-title">{r.title}</div>
                <div className="cd-emp-row-sub">{r.sub}</div>
              </div>
            </label>
          ))}
        </div>

        <button className="cd-emp-add-btn">
          Add To Recommendation Drawer →
        </button>
      </div>

      {/* Assign Modal */}
      <AssignModal
        isOpen={assignModalOpen}
        onClose={handleCloseAssignModal}
        onConfirm={handleConfirmAssign}
        title="Assign Diagnostic Form"
        options={employeeOptions}
        selectLabel="Staff assign"
        selectPlaceholder="Select Staff"
        selectedValue={selectedStaff}
        onSelectChange={setSelectedStaff}
      />
    </div>
  );
}
